import React, {useContext, useEffect} from "react";
import {observer} from "mobx-react";
import {Checkbox, Form, Radio, Switch, message} from "antd";
import {useRequiredLayers} from "../../../../utils/context";
import {CartCreateStore, CartStoreContext} from "../cart.create.store";
import {CartCreateController, CartCreateControllerContext} from "../cart.create.controller";
import {useTranslation} from "react-i18next";
import {RootStoreContext} from "../../../../stores/root/root";
import {isAllowed} from "../../../../utils/helpers";
import "./cart.create.warehouses.scss";

export const CartCreateWarehouses: React.FC = observer(() => {
    const {store, controller} = useRequiredLayers<CartCreateStore, CartCreateController>(CartStoreContext, CartCreateControllerContext);
    const {authStore: {attributes, lz}} = useContext(RootStoreContext);
    const {t} = useTranslation("translation", {useSuspense: false});
    const [form] = Form.useForm();
    const warehouses = store.defaultWarehouses;
    const useBackorder = store.useBackorder;
    const busy = store.loading || store.previewLoading || store.checkoutButtonDisabled;
    const selectedWarehouses = lz.mw ? warehouses: warehouses.slice(0, 1);

    useEffect(() => {
        form.setFieldsValue({
            warehouses: lz.mw ? warehouses : warehouses.slice(0, 1),
            useBackorder,
        });
        if (!lz.mw && warehouses.length > 1) {
            void controller.updateCart({
                warehouses: warehouses.slice(0, 1),
                useBackorder,
            }).catch(() => message.error(t("ERROR")));
        }
    }, [controller, form, warehouses, useBackorder, lz.mw, t]);

    return (
        <Form
            form={form}
            className="cart-warehouse-bar"
            name="cart_form"
            initialValues={{warehouses: selectedWarehouses, useBackorder}}
            onValuesChange={(_, values) => {
                const nextWarehouses = Array.isArray(values.warehouses) ? values.warehouses : [];
                if (nextWarehouses.length === 0) {
                    form.setFieldsValue({warehouses: selectedWarehouses});
                    return;
                }
                void controller.updateCart({
                    warehouses: nextWarehouses,
                    useBackorder: Boolean(values.useBackorder),
                }).catch(() => message.error(t("ERROR")));
            }}
        >
            <div className="cart-warehouse-picker">
                <span className="cart-warehouse-label">{String(t("ORDERS.CREATE.CALCULATE_BY")).trim()}:</span>
                {lz.mw ? (
                    <Form.Item name="warehouses" noStyle>
                        <Checkbox.Group aria-label={t("ORDERS.CREATE.CALCULATE_BY")}>
                            {store.warehousesAll.map(warehouse => (
                                <Checkbox
                                    className="cart-warehouse-option"
                                    disabled={busy || (selectedWarehouses.length === 1 && selectedWarehouses[0] === warehouse.id)}
                                    key={warehouse.id}
                                    value={warehouse.id}
                                >{warehouse.name}</Checkbox>
                            ))}
                        </Checkbox.Group>
                    </Form.Item>
                ) : (
                    <Form.Item
                        name="warehouses"
                        noStyle
                        getValueProps={(selected: string[] = []) => ({value: selected[0]})}
                        getValueFromEvent={event => [event.target.value]}
                    >
                        <Radio.Group disabled={busy} aria-label={t("ORDERS.CREATE.CALCULATE_BY")}>
                            {store.warehousesAll.map(warehouse => (
                                <Radio className="cart-warehouse-option" key={warehouse.id} value={warehouse.id}>
                                    {warehouse.name}
                                </Radio>
                            ))}
                        </Radio.Group>
                    </Form.Item>
                )}
            </div>
            {isAllowed(attributes, "order_backorder") && (
                <label className="cart-backorder-toggle">
                    <Form.Item noStyle name="useBackorder" valuePropName="checked">
                        <Switch size="small" disabled={busy} aria-label={t("ORDER.CREATE.USE_BACKORDER")} />
                    </Form.Item>
                    <span>{t("ORDER.CREATE.USE_BACKORDER")}</span>
                </label>
            )}
        </Form>
    );
});
